<template>
    <Head title="Create Template" />

    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
            <div>
                <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Create Checklist Template</h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Start by entering basic template information</p>
            </div>
            <Link
                :href="route('checklists.templates.index')"
                class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </Link>
        </div>

        <!-- Form -->
        <form @submit.prevent="submit" class="space-y-6">
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <div class="space-y-4">
                    <!-- Type (First Field) -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Template Type <span class="text-red-500">*</span>
                        </label>
                        <select
                            v-model="form.type_id"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            required
                        >
                            <option value="">Select Type</option>
                            <option v-for="type in types" :key="type.id" :value="type.id">
                                {{ type.name }}
                            </option>
                        </select>
                        <p v-if="errors.type_id" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.type_id }}</p>

                        <!-- Type Description (shown when type is selected) -->
                        <div v-if="selectedType" class="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                            <p class="text-sm text-gray-700 dark:text-gray-300">{{ selectedType.description }}</p>
                        </div>
                    </div>

                    <!-- Title -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Template Title <span class="text-red-500">*</span>
                        </label>
                        <input
                            v-model="form.title"
                            type="text"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            required
                        />
                        <p v-if="errors.title" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.title }}</p>
                    </div>

                    <!-- Description -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Description
                        </label>
                        <textarea
                            v-model="form.description"
                            rows="3"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                    </div>

                    <!-- Scoring (only show when selected type supports scoring) -->
                    <div v-if="selectedType && selectedType.supports_scoring" class="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <label class="flex items-center">
                            <input
                                v-model="form.scoring_enabled"
                                type="checkbox"
                                class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                            <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Enable Scoring</span>
                        </label>

                        <!-- Scoring Method (only show when scoring is enabled) -->
                        <div v-if="form.scoring_enabled" class="mt-4 ml-6">
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Scoring Method <span class="text-red-500">*</span>
                            </label>
                            <div class="space-y-2">
                                <label class="flex items-center">
                                    <input
                                        v-model="form.scoring_method"
                                        type="radio"
                                        value="percentage"
                                        class="border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                        Percentage-based (0-100%)
                                    </span>
                                </label>
                                <label class="flex items-center">
                                    <input
                                        v-model="form.scoring_method"
                                        type="radio"
                                        value="points"
                                        class="border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                        Points-based (custom point values)
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <div class="flex justify-end space-x-3">
                <Link
                    :href="route('checklists.templates.index')"
                    class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                    Cancel
                </Link>
                <button
                    type="submit"
                    :disabled="form.processing"
                    class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                    Create & Continue to Builder
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import route from '@/Utils/route';
import { useSecureForm } from '@/Composables/useSecureForm';

const props = defineProps({
    types: Array,
    errors: Object,
    companyOwner: {
        type: Object,
        default: null,
    },
});

const form = useSecureForm({
    title: '',
    description: '',
    type_id: '',
    scoring_enabled: false,
    scoring_method: 'percentage', // Default to percentage
});

// Get the selected type object based on form.type_id
const selectedType = computed(() => {
    if (!form.type_id) return null;
    return props.types.find(type => type.id === form.type_id);
});

const submit = () => {
    form.post(route('checklists.templates.store'));
};
</script>
